// src/pages/administrasi/RealisasiNota.jsx
//
// Bendahara's Realisasi Nota ledger — one row per actual purchase, each
// backed by a photographed nota/kwitansi. Distinct from the "Kas Keluar"
// nav item (a generated Bukti Kas Keluar letter, see AdmCategoryPage): this
// is the structured expense log that feeds LPJ, matching the columns real
// SPJ/realisasi spreadsheets already use (Toko, Pembeli, Tanggal, Harga,
// Nota).
//
// Easy-entry flow: pick/snap a nota photo -> it's sent to the OCR-preview
// endpoint (Tesseract) which guesses toko/tanggal/total -> those pre-fill
// the form for the user to review/correct -> Save uploads the photo to the
// event's Bendahara/Nota Drive folder and records the entry.
import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { API_BASE_URL } from "../../utils/config";
import { responseErrorMessage } from "../../utils/apiError";
import CustomSelect from "../../components/CustomSelect";
import RupiahInput from "../../components/RupiahInput";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) throw new Error(await responseErrorMessage(res));
  return res.status === 204 ? null : res.json();
}

const fmtRp = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const resolveUrl = (u) => (u && u.startsWith("/") ? `${API_BASE_URL}${u}` : u);

const emptyForm = {
  rab_item: "",
  section: "",
  kode: "",
  uraian: "",
  toko: "",
  pembeli: "",
  tanggal: "",
  harga_satuan: "",
  qty: 1,
  satuan: "",
};

// Quick picks shown first in the Satuan field; anything else already used in
// this event's RAB / nota is appended after them (see satuanOptions).
const SATUAN_UTAMA = ["Pcs", "Set", "Unit"];

export default function RealisasiNota() {
  const [events, setEvents] = useState([]);
  const [eventId, setEventId] = useState("");
  const [items, setItems] = useState([]);
  const [rabItems, setRabItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoIsPdf, setPhotoIsPdf] = useState(false);
  const [ocrRawText, setOcrRawText] = useState("");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrError, setOcrError] = useState("");
  const [showRawText, setShowRawText] = useState(false);
  const [saving, setSaving] = useState(false);
  const [repushingId, setRepushingId] = useState(null);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    apiFetch("/api/administrasi/events/?active_only=1")
      .then((data) => {
        const list = Array.isArray(data) ? data : data.results || [];
        setEvents(list);
        if (list.length) setEventId(String(list[0].id));
      })
      .catch((e) => setError(e.message));
  }, []);

  const loadItems = useCallback(async () => {
    if (!eventId) {
      setItems([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await apiFetch(`/api/administrasi/kas-keluar/?event_id=${eventId}`);
      setItems(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!eventId) {
      setRabItems([]);
      return;
    }
    apiFetch(`/api/administrasi/rab-items/?event_id=${eventId}`)
      .then((data) => setRabItems(Array.isArray(data) ? data : data.results || []))
      .catch(() => setRabItems([]));
  }, [eventId]);

  // Section/Kode suggestions come from this event's own Anggaran (RAB) plan
  // rather than a generic seed list — that's what actually keeps "konsumsi"
  // from drifting into "konsumai"/"Konsumsi" across entries, since Bendahara
  // is picking from what was already budgeted instead of retyping free text.
  const sectionOptions = useMemo(() => {
    const fromRab = rabItems.map((r) => r.section).filter(Boolean);
    const fromItems = items.map((it) => it.section).filter(Boolean);
    return Array.from(new Set([...fromRab, ...fromItems]));
  }, [rabItems, items]);

  const kodeOptions = useMemo(() => {
    const sectionLower = form.section.trim().toLowerCase();
    const fromRab = rabItems
      .filter((r) => (r.section || "").toLowerCase() === sectionLower)
      .map((r) => r.kode)
      .filter(Boolean);
    const fromItems = items
      .filter((it) => (it.section || "").toLowerCase() === sectionLower)
      .map((it) => it.kode)
      .filter(Boolean);
    return Array.from(new Set([...fromRab, ...fromItems]));
  }, [rabItems, items, form.section]);

  const satuanOptions = useMemo(() => {
    const used = [...rabItems, ...items].map((r) => (r.satuan || "").trim()).filter(Boolean);
    const seen = new Set();
    return [...SATUAN_UTAMA, ...used].filter((s) => {
      const key = s.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [rabItems, items]);

  const handleRabItemChange = (value) => {
    setForm((f) => {
      if (!value) return { ...f, rab_item: value };
      const linked = rabItems.find((r) => String(r.id) === value);
      return {
        ...f,
        rab_item: value,
        section: linked?.section || f.section,
        kode: linked?.kode || f.kode,
        // Only a starting suggestion: the nota can be in a different unit
        // than what was budgeted (RAB "Paket", nota "Set"), so unlike
        // section/kode it stays editable and never overwrites what was typed.
        satuan: f.satuan || linked?.satuan || "",
      };
    });
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setPhotoFile(null);
    setPhotoPreview("");
    setPhotoIsPdf(false);
    setOcrRawText("");
    setOcrError("");
    setShowRawText(false);
    setFormError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      rab_item: it.rab_item ? String(it.rab_item) : "",
      section: it.section || "",
      kode: it.kode || "",
      uraian: it.uraian,
      toko: it.toko || "",
      pembeli: it.pembeli || "",
      tanggal: it.tanggal || "",
      harga_satuan: String(it.harga_satuan),
      qty: it.qty,
      satuan: it.satuan || "",
    });
    setPhotoFile(null);
    setPhotoPreview(resolveUrl(it.nota_photo_url) || "");
    setPhotoIsPdf((it.nota_photo_url || "").toLowerCase().endsWith(".pdf"));
    setOcrRawText(it.ocr_raw_text || "");
    setOcrError("");
    setShowRawText(false);
    setFormError("");
    setShowForm(true);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isPdf = file.type === "application/pdf";
    setPhotoFile(file);
    setPhotoIsPdf(isPdf);
    setPhotoPreview(isPdf ? "" : URL.createObjectURL(file));
    setOcrError("");
    setOcrRawText("");
    if (isPdf) {
      // Faktur digital — no OCR to run, fields stay as the user typed them.
      return;
    }
    setOcrLoading(true);
    try {
      const body = new FormData();
      body.append("photo", file);
      const result = await apiFetch("/api/administrasi/kas-keluar/ocr-preview/", {
        method: "POST",
        body,
      });
      if (result.error) setOcrError(result.error);
      setOcrRawText(result.raw_text || "");
      setForm((f) => ({
        ...f,
        toko: f.toko || result.toko || "",
        tanggal: f.tanggal || result.tanggal || "",
        harga_satuan: f.harga_satuan || (result.total ? String(result.total) : ""),
      }));
    } catch (err) {
      setOcrError(err.message);
    } finally {
      setOcrLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.uraian.trim()) {
      setFormError("Uraian wajib diisi.");
      return;
    }
    if (!form.satuan.trim()) {
      setFormError("Satuan wajib diisi (mis. Set, Pcs, Unit).");
      return;
    }
    if (!editingId && !photoFile) {
      setFormError("Foto nota wajib dilampirkan.");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/api/administrasi/kas-keluar/${editingId}/`, {
          method: "PATCH",
          body: JSON.stringify({
            rab_item: form.rab_item ? Number(form.rab_item) : null,
            section: form.section.trim(),
            kode: form.kode.trim(),
            uraian: form.uraian.trim(),
            toko: form.toko.trim(),
            pembeli: form.pembeli.trim(),
            tanggal: form.tanggal || null,
            harga_satuan: Number(form.harga_satuan) || 0,
            qty: Number(form.qty) || 1,
            satuan: form.satuan.trim(),
          }),
        });
      } else {
        const body = new FormData();
        body.append("event_id", eventId);
        if (form.rab_item) body.append("rab_item_id", form.rab_item);
        body.append("section", form.section.trim());
        body.append("kode", form.kode.trim());
        body.append("uraian", form.uraian.trim());
        body.append("toko", form.toko.trim());
        body.append("pembeli", form.pembeli.trim());
        if (form.tanggal) body.append("tanggal", form.tanggal);
        body.append("harga_satuan", String(Number(form.harga_satuan) || 0));
        body.append("qty", String(Number(form.qty) || 1));
        body.append("satuan", form.satuan.trim());
        body.append("ocr_raw_text", ocrRawText);
        body.append("photo", photoFile);
        await apiFetch("/api/administrasi/kas-keluar/", { method: "POST", body });
      }
      setShowForm(false);
      resetForm();
      loadItems();
    } catch (err) {
      setFormError(err.message);
      // A failed POST may still have saved on the server (a gateway timeout cuts
      // the reply, not the save). Refresh so a saved row shows up in the list
      // instead of being re-entered blind as a duplicate.
      if (!editingId) loadItems();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Hapus entri "${it.uraian}"?`)) return;
    try {
      await apiFetch(`/api/administrasi/kas-keluar/${it.id}/`, { method: "DELETE" });
      loadItems();
    } catch (err) {
      alert(err.message);
    }
  };

  // Retries the Drive upload for a nota whose first upload failed — the
  // file is already stored on the server, so nothing is re-attached here.
  const handleRepush = async (it) => {
    setError("");
    setRepushingId(it.id);
    try {
      await apiFetch(`/api/administrasi/kas-keluar/${it.id}/repush-drive/`, { method: "POST" });
      loadItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setRepushingId(null);
    }
  };

  const grandTotal = items.reduce((sum, it) => sum + it.total, 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-4">
        <h1 className="text-2xl font-semibold">Realisasi Nota</h1>
        <button
          onClick={openCreate}
          disabled={!eventId}
          className="shrink-0 px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
        >
          + Tambah Pengeluaran
        </button>
      </div>

      <div className="glass-card p-4 mb-4">
        <label className="block text-xs font-medium mb-1">Event</label>
        <CustomSelect
          value={eventId}
          onChange={setEventId}
          options={events.map((ev) => ({ value: String(ev.id), label: ev.name }))}
          placeholder="-- Pilih event --"
        />
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      <div className="glass-card p-4 mb-4">
        <div className="glass-table-wrap overflow-x-auto">
          <table className="glass-table text-xs">
            <thead>
              <tr>
                <th className="text-left">Tanggal</th>
                <th className="text-left">Toko</th>
                <th className="text-left">Uraian</th>
                <th className="text-left">Pembeli</th>
                <th className="text-right">Qty</th>
                <th className="text-left">Satuan</th>
                <th className="text-right">Harga</th>
                <th className="text-right">Total</th>
                <th className="text-left">Nota</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} className="border-t">
                  <td className="px-2 py-1.5 whitespace-nowrap">{it.tanggal || "-"}</td>
                  <td className="px-2 py-1.5">{it.toko || "-"}</td>
                  <td className="px-2 py-1.5">{it.uraian}</td>
                  <td className="px-2 py-1.5">{it.pembeli || "-"}</td>
                  <td className="px-2 py-1.5 text-right">{it.qty}</td>
                  <td className="px-2 py-1.5">{it.satuan || "-"}</td>
                  <td className="px-2 py-1.5 text-right">{fmtRp(it.harga_satuan)}</td>
                  <td className="px-2 py-1.5 text-right font-medium">{fmtRp(it.total)}</td>
                  <td className="px-2 py-1.5">
                    <a
                      href={it.drive_file_url || resolveUrl(it.nota_photo_url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-brand-600 hover:underline"
                    >
                      Lihat
                    </a>
                    {!it.drive_file_url && (
                      <>
                        <span className="ml-1 text-amber-600" title="Belum terupload ke Drive">
                          ⚠
                        </span>
                        <button
                          onClick={() => handleRepush(it)}
                          disabled={repushingId === it.id}
                          className="ml-1 text-amber-600 hover:underline disabled:opacity-50"
                        >
                          {repushingId === it.id ? "Mengirim..." : "Push ulang"}
                        </button>
                      </>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(it)}
                      className="text-brand-600 hover:underline mr-2"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(it)}
                      className="text-red-500 hover:underline"
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!loading && items.length === 0 && (
          <p className="text-sm text-muted mt-3">Belum ada pengeluaran untuk event ini.</p>
        )}
      </div>

      {items.length > 0 && (
        <div className="glass-card p-4 flex items-center justify-between">
          <span className="font-semibold">TOTAL REALISASI</span>
          <span className="font-semibold text-lg">{fmtRp(grandTotal)}</span>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 overflow-y-auto">
          <div className="glass-card w-full max-w-lg p-6 my-8">
            <h2 className="text-lg font-semibold mb-4">
              {editingId ? "Edit Pengeluaran" : "Tambah Pengeluaran (Nota)"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-3">
              {!editingId && (
                <div>
                  <label className="block text-xs font-medium mb-1">Foto Nota / Faktur Digital</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    onChange={handlePhotoChange}
                    className="w-full text-sm"
                  />
                  <p className="text-xs text-muted mt-1">Foto nota fisik (JPG/PNG) atau faktur digital (PDF).</p>
                  {ocrLoading && <p className="text-xs text-muted mt-1">Membaca nota (OCR)...</p>}
                  {ocrError && (
                    <p className="text-xs text-amber-600 mt-1">
                      OCR gagal membaca otomatis ({ocrError}) — isi manual di bawah.
                    </p>
                  )}
                </div>
              )}

              {photoIsPdf && (
                <p className="text-sm bg-black/5 dark:bg-white/5 rounded-md p-2">
                  {photoFile ? (
                    <>📄 {photoFile.name} (faktur PDF — tanpa OCR, isi field manual)</>
                  ) : (
                    <a href={photoPreview} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
                      📄 Lihat faktur PDF
                    </a>
                  )}
                </p>
              )}

              {photoPreview && !photoIsPdf && (
                <div>
                  <img
                    src={photoPreview}
                    alt="Preview nota"
                    className="max-h-48 rounded-md border border-white/50 dark:border-white/15 mx-auto"
                  />
                  {ocrRawText && (
                    <button
                      type="button"
                      onClick={() => setShowRawText((v) => !v)}
                      className="text-xs text-brand-600 hover:underline mt-1"
                    >
                      {showRawText ? "Sembunyikan" : "Lihat"} teks hasil OCR
                    </button>
                  )}
                  {showRawText && (
                    <pre className="text-[10px] whitespace-pre-wrap bg-black/5 dark:bg-white/5 rounded-md p-2 mt-1 max-h-32 overflow-y-auto">
                      {ocrRawText}
                    </pre>
                  )}
                </div>
              )}

              <div>
                <label className="block text-xs font-medium mb-1">Uraian</label>
                <input
                  type="text"
                  value={form.uraian}
                  onChange={(e) => setForm((f) => ({ ...f, uraian: e.target.value }))}
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Kaitkan ke Item RAB (opsional)</label>
                <CustomSelect
                  value={form.rab_item}
                  onChange={handleRabItemChange}
                  options={[
                    { value: "", label: "-- Tidak dikaitkan --" },
                    ...rabItems.map((r) => ({ value: String(r.id), label: `${r.section ? r.section + " — " : ""}${r.uraian}` })),
                  ]}
                  placeholder="-- Tidak dikaitkan --"
                />
                <p className="text-xs text-muted mt-1">
                  {form.rab_item
                    ? "Section & Kode di bawah otomatis ikut item RAB ini (terkunci)."
                    : "Kalau dikaitkan, Section & Kode otomatis terisi dari item RAB dan terkunci."}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Toko</label>
                  <input
                    type="text"
                    value={form.toko}
                    onChange={(e) => setForm((f) => ({ ...f, toko: e.target.value }))}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Pembeli</label>
                  <input
                    type="text"
                    value={form.pembeli}
                    onChange={(e) => setForm((f) => ({ ...f, pembeli: e.target.value }))}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Tanggal</label>
                  <input
                    type="date"
                    value={form.tanggal}
                    onChange={(e) => setForm((f) => ({ ...f, tanggal: e.target.value }))}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">Section</label>
                  <input
                    type="text"
                    list="realisasi-section-options"
                    value={form.section}
                    disabled={!!form.rab_item}
                    onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
                    placeholder="mis. Listrik, Sound"
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm disabled:opacity-60 disabled:bg-black/5 dark:disabled:bg-white/5"
                  />
                  <datalist id="realisasi-section-options">
                    {sectionOptions.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Kode</label>
                <input
                  type="text"
                  list="realisasi-kode-options"
                  value={form.kode}
                  disabled={!!form.rab_item}
                  onChange={(e) => setForm((f) => ({ ...f, kode: e.target.value }))}
                  placeholder="mis. Pengadaan Barang, Perawatan, Sewa"
                  className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm disabled:opacity-60 disabled:bg-black/5 dark:disabled:bg-white/5"
                />
                <datalist id="realisasi-kode-options">
                  {kodeOptions.map((k) => (
                    <option key={k} value={k} />
                  ))}
                </datalist>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium mb-1">Qty</label>
                  <input
                    type="number"
                    min={1}
                    value={form.qty}
                    onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">
                    Satuan <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    list="realisasi-satuan-options"
                    value={form.satuan}
                    onChange={(e) => setForm((f) => ({ ...f, satuan: e.target.value }))}
                    placeholder="Set / Pcs / Unit"
                    required
                    maxLength={50}
                    className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-2 text-sm"
                  />
                  <datalist id="realisasi-satuan-options">
                    {satuanOptions.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                  <p className="text-xs text-muted mt-1">Mis. sewa alat = Set.</p>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1">Harga (Total Nota)</label>
                <RupiahInput
                  value={form.harga_satuan}
                  onChange={(v) => setForm((f) => ({ ...f, harga_satuan: v }))}
                />
              </div>

              {formError && <p className="text-red-600 text-xs">{formError}</p>}

              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 rounded-lg border border-white/50 dark:border-white/15 text-sm"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving || ocrLoading}
                  className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-50"
                >
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
